
import React from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
	import { 
	    Box, 
	    Typography, 
	    Container, 
	    Button,
	    Grid,
	    Stack,
	    Divider,
	    Paper,
	    alpha
	} from '@mui/material';
	import ArrowBackIcon from '@mui/icons-material/ArrowBack';
	import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
	import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
	import BoltIcon from '@mui/icons-material/Bolt';

	const PartnerPage = () => {
	    const { id } = useParams();
	    const navigate = useNavigate();
	    const brandRed = '#CC0000';
	    const offBlack = '#0a1128';
    
    // Antigravity Design Tokens
    const spacing = {
        section: { xs: 8, md: 12 },
        element: { xs: 2, md: 4 }
    };

    // Mock Database
    const partners = {
        'microsoft': {
            name: 'Microsoft',
            tier: 'Gold Partner',
            tagline: 'Potencializando a Transformação Digital',
            description: 'Como parceiros Gold da Microsoft, entregamos excelência em infraestrutura Azure, Dynamics 365 e Power Platform. Unimos nossa expertise em IA com o ecossistema mais robusto do mercado para criar soluções de missão crítica.',
            stats: ['10+ Anos de Parceria', '100+ Projetos Azure', 'Certificação Avançada'],
            features: ['Cloud Computing (Azure)', 'Enterprise AI', 'Modern Workplace'],
            color: '#00a4ef' // Microsoft Blue vibe
        },
        'google': {
            name: 'Google Cloud',
            tier: 'Premier Partner',
            tagline: 'Inovação em Dados e AI',
            description: 'Aceleramos a jornada de dados das organizações usando o poder do Google Cloud Platform. De BigQuery a TensorFlow, construímos o futuro da inteligência analítica.',
            stats: ['Premier Tier', 'Specialization in Data', 'AI First'],
            features: ['Big Data & Analytics', 'Machine Learning', 'Kubernetes (GKE)'],
            color: '#4285F4'
        },
        'aws': {
            name: 'AWS',
            tier: 'Consulting Partner',
            tagline: 'Infraestrutura de Escala Global',
            description: 'Expertise profunda em Amazon Web Services para migração, otimização e gerenciamento de ambientes em nuvem seguros, resilientes e de alta performance.',
            stats: ['AWS Certified', 'Global Scale', 'Security First'],
            features: ['Cloud Migration', 'Serverless Computing', 'DevSecOps'],
            color: '#FF9900'
        },
        'salesforce': {
            name: 'Salesforce',
            tier: 'Strategic Partner',
            tagline: 'Conectando Clientes e Marcas',
            description: 'Implementação e personalização do CRM #1 do mundo. Criamos jornadas de cliente unificadas e estratégias de engajamento baseadas em dados.',
            stats: ['CRM Leaders', 'Customer 360', 'Marketing Cloud'],
            features: ['CRM Strategy', 'Marketing Automation', 'Customer Experience'],
            color: '#00A1E0'
        }
    };

    const clients = [
        'Banco do Brasil', 'Caixa', 'Governo Federal', 'Eletrobras', 'Petrobras', 'Vale'
    ];

    const currentPartner = partners[id] || partners['microsoft'];

    // Scroll to top on navigation
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    return (
        <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', width: '100%', overflow: 'hidden' }}>
            
            {/* 1. HERO SECTION (IMMERSIVE) */}
            <Box 
                component="section"
                sx={{ 
                    position: 'relative', 
                    bgcolor: offBlack, 
                    color: 'white', 
                    pt: { xs: 16, md: 24 }, 
                    pb: { xs: 12, md: 16 },
                    overflow: 'hidden'
                }}
            >
                {/* Abstract Background */}
                <Box sx={{ 
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.1,
                    backgroundImage: 'radial-gradient(circle at 80% 20%, #ffffff 0%, transparent 40%), radial-gradient(circle at 20% 80%, #CC0000 0%, transparent 40%)',
                    zIndex: 0
                }} />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Button 
                        startIcon={<ArrowBackIcon />} 
                        onClick={() => navigate('/')}
                        sx={{ 
                            color: alpha('#fff', 0.6), 
                            mb: 4, 
                            textTransform: 'none',
                            '&:hover': { color: '#fff', bgcolor: 'transparent' } 
                        }}
                    >
                        Voltar ao Portal
                    </Button>

                    <Stack spacing={3} maxWidth="800px">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: '40px', height: '2px', bgcolor: brandRed }} />
                            <Typography variant="overline" sx={{ letterSpacing: '0.2em', fontWeight: 700, color: alpha('#fff', 0.8) }}>
                                ALIANÇA ESTRATÉGICA
                            </Typography>
                        </Box>
                        
                        <Typography 
                            variant="h1" 
                            sx={{ 
                                fontSize: { xs: '3rem', md: '5rem' }, 
                                fontWeight: 800, 
                                lineHeight: 1.1,
                                letterSpacing: '-0.02em',
                                background: 'linear-gradient(to right, #fff, #ccc)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}
                        >
                            {currentPartner.name}
                        </Typography>

                        <Typography 
                            variant="h4" 
                            sx={{ 
                                fontSize: { xs: '1.25rem', md: '1.75rem' }, 
                                color: alpha('#fff', 0.7), 
                                fontWeight: 300, 
                                lineHeight: 1.5,
                                maxWidth: '90%' 
                            }}
                        >
                            {currentPartner.tagline}
                        </Typography>

                        <Stack direction="row" spacing={2} sx={{ pt: 4 }}>
                            <Button 
                                component={RouterLink} 
                                to="/fale-conosco"
                                variant="contained" 
                                size="large"
                                sx={{ 
                                    bgcolor: brandRed, 
                                    color: 'white', 
                                    px: 5, 
                                    py: 1.5, 
                                    fontSize: '1rem', 
                                    fontWeight: 700,
                                    borderRadius: '14px', // Pill shape
                                    '&:hover': { bgcolor: '#CC0000' } 
                                }}
                            >
                                Falar com Especialista
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>

            {/* 2. VALUE PROPOSITION & STATS */}
            <Container maxWidth="lg" sx={{ py: spacing.section }}>
                <Grid container spacing={8} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography variant="overline" sx={{ color: brandRed, fontWeight: 800, letterSpacing: '0.1em' }}>
                            {currentPartner.tier.toUpperCase()}
                        </Typography>
                        <Typography variant="h3" sx={{ mt: 2, mb: 4, fontWeight: 700, color: offBlack }}>
                            Por que {currentPartner.name}?
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#444' }}>
                            {currentPartner.description}
                        </Typography>
                        
                        <Stack spacing={2} sx={{ mt: 4 }}>
                            {currentPartner.features.map((feature) => (
                                <Stack key={feature} direction="row" alignItems="center" spacing={2}>
                                    <VerifiedUserIcon sx={{ color: brandRed }} />
                                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                        {feature}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Grid container spacing={4}>
                            {currentPartner.stats.map((stat, index) => (
                                <Grid item xs={12} key={index}>
                                    <Paper 
                                        elevation={0}
                                        sx={{ 
                                            p: 4, 
                                            bgcolor: '#f8f8f8', 
                                            borderLeft: `4px solid ${brandRed}`,
                                            transition: 'transform 0.3s ease',
                                            '&:hover': { transform: 'translateX(10px)', bgcolor: '#f0f0f0' }
                                        }}
                                    >
                                        <BoltIcon sx={{ fontSize: 40, color: offBlack, mb: 1, opacity: 0.5 }} />
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: offBlack }}>
                                            {stat}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Container>

            <Divider />

            {/* 3. TRUSTED CLIENTS (ECOSYSTEM) */}
            <Box sx={{ py: spacing.section, bgcolor: '#FAFAFA' }}>
                <Container maxWidth="lg">
                    <Typography 
                        variant="h6" 
                        align="center" 
                        sx={{ 
                            mb: 6, 
                            fontWeight: 700, 
                            textTransform: 'uppercase', 
                            letterSpacing: '0.15em', 
                            color: alpha(offBlack, 0.5) 
                        }}
                    >
                        Clientes que aceleramos com {currentPartner.name}
                    </Typography>
                    
                    <Grid container spacing={4} justifyContent="center">
                        {clients.map((client) => (
                            <Grid item key={client} xs={6} sm={4} md={2}>
                                <Box sx={{ 
                                    height: '80px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    opacity: 0.4,
                                    transition: 'all 0.3s ease',
                                    '&:hover': { opacity: 1, transform: 'scale(1.05)' }
                                }}>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: offBlack }}>
                                        {client}
                                    </Typography>
                                    {/* Ideally logos would go here */}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* 4. STRATEGIC NETWORK (NAVIGATION) */}
            <Box sx={{ py: spacing.section, bgcolor: offBlack, color: 'white' }}>
                <Container maxWidth="lg">
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 8 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700 }}>
                            Mais Alianças Estratégicas
                        </Typography>
                        <Button 
                            endIcon={<ArrowForwardIcon />} 
                            component={RouterLink} 
                            to="/fale-conosco"
                            sx={{ color: 'white', borderColor: 'white', '&:hover': { bgcolor: 'white', color: offBlack } }} 
                            variant="outlined"
                        >
                            Ver Todas
                        </Button>
                    </Stack>
                    
                    <Grid container spacing={4}>
                        {Object.entries(partners).filter(([key]) => key !== id).map(([key, p]) => (
                            <Grid item xs={12} md={4} key={key}>
                                <Paper 
                                    component={RouterLink}
                                    to={`/parceiros/${key}`}
                                    elevation={0}
                                    sx={{ 
                                        p: 4, 
                                        height: '100%', 
                                        bgcolor: alpha('#fff', 0.05), 
                                        color: 'white',
                                        textDecoration: 'none',
                                        transition: 'all 0.4s ease',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        display: 'block',
                                        '&:hover': { 
                                            bgcolor: brandRed, 
                                            borderColor: brandRed,
                                            transform: 'translateY(-8px)'
                                        }
                                    }}
                                >
                                    <Typography variant="overline" sx={{ color: alpha('#fff', 0.6) }}>
                                        {p.tier}
                                    </Typography>
                                    <Typography variant="h5" sx={{ mt: 1, mb: 2, fontWeight: 700 }}>
                                        {p.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.7), lineHeight: 1.6 }}>
                                        {p.tagline}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

        </Box>
    );
};

export default PartnerPage;
